import React, { useState } from 'react';
import { X, Search, CheckSquare, Tag, Clock, AlertCircle } from 'lucide-react';
import { formatEstimatedEffort } from '../utils/effortFormatting';

const TaskTemplateSelector = ({ 
  isOpen, 
  onClose, 
  taskTemplates = [], 
  selectedTaskIds = [], 
  onSave 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState([...selectedTaskIds]);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');

  // Get unique categories and priorities
  const categories = ['all', ...new Set(taskTemplates.map(t => t.category).filter(Boolean))];
  const priorities = ['all', 'Low', 'Normal', 'High', 'Critical'];

  const filteredTemplates = taskTemplates.filter(task => {
    const matchesSearch = task.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         task.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         task.category?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || task.category === filterCategory;
    const matchesPriority = filterPriority === 'all' || task.priority === filterPriority;
    
    return matchesSearch && matchesCategory && matchesPriority;
  });

  const handleToggle = (taskId) => {
    setSelectedIds(prev => 
      prev.includes(taskId) 
        ? prev.filter(id => id !== taskId)
        : [...prev, taskId]
    );
  };

  const handleSelectAll = () => {
    const allFilteredIds = filteredTemplates.map(t => t.id);
    setSelectedIds(prev => {
      const allSelected = allFilteredIds.every(id => prev.includes(id));
      if (allSelected) {
        // Deselect all filtered
        return prev.filter(id => !allFilteredIds.includes(id));
      } else {
        // Select all filtered
        return [...new Set([...prev, ...allFilteredIds])];
      }
    });
  };

  const handleSave = () => {
    onSave(selectedIds);
    onClose();
  };

  // formatEstimatedEffort is now imported from ../utils/effortFormatting

  if (!isOpen) return null;

  const allFilteredSelected = filteredTemplates.length > 0 && 
    filteredTemplates.every(t => selectedIds.includes(t.id));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Link Task Templates</h2>
            <p className="text-sm text-gray-500 mt-1">
              Select task templates to automatically create when this project is created
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Filters & Search */}
        <div className="p-6 border-b border-gray-200 space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search task templates by name, description, or category..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          {/* Filters */}
          <div className="flex items-center space-x-4">
            <div className="flex-1">
              <label className="text-xs font-medium text-gray-700 mb-1 block">Category</label>
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>
                    {cat === 'all' ? 'All Categories' : cat}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex-1">
              <label className="text-xs font-medium text-gray-700 mb-1 block">Priority</label>
              <select
                value={filterPriority}
                onChange={(e) => setFilterPriority(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                {priorities.map(pri => (
                  <option key={pri} value={pri}>
                    {pri === 'all' ? 'All Priorities' : pri}
                  </option>
                ))}
              </select>
            </div>

            <div className="pt-5">
              <button
                onClick={handleSelectAll}
                className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                {allFilteredSelected ? 'Deselect All' : 'Select All'}
              </button>
            </div>
          </div>

          {/* Selection Count */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">
              {selectedIds.length} of {taskTemplates.length} task templates selected
            </span>
            {selectedIds.length > 0 && (
              <button
                onClick={() => setSelectedIds([])}
                className="text-purple-600 hover:text-purple-700 font-medium"
              >
                Clear All
              </button>
            )}
          </div>
        </div>

        {/* Task List */}
        <div className="flex-1 overflow-y-auto p-6">
          {filteredTemplates.length === 0 ? (
            <div className="text-center py-12">
              <CheckSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">
                {searchTerm || filterCategory !== 'all' || filterPriority !== 'all'
                  ? 'No task templates match your filters'
                  : 'No task templates available. Create task templates first.'}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredTemplates.map((task) => {
                const isSelected = selectedIds.includes(task.id);
                return (
                  <div
                    key={task.id}
                    onClick={() => handleToggle(task.id)}
                    className={`p-4 border rounded-lg cursor-pointer transition-all ${
                      isSelected
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {}} // Handled by parent div click
                        className="mt-1 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-1">
                          <h4 className="text-sm font-medium text-gray-900">{task.name}</h4>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            task.priority === 'Critical' ? 'bg-red-100 text-red-700' :
                            task.priority === 'High' ? 'bg-orange-100 text-orange-700' :
                            task.priority === 'Normal' ? 'bg-blue-100 text-blue-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {task.priority}
                          </span>
                        </div>
                        {task.description && (
                          <p className="text-sm text-gray-600 mb-2 line-clamp-2">{task.description}</p>
                        )}
                        <div className="flex items-center space-x-4 text-xs text-gray-500">
                          {task.category && (
                            <span className="flex items-center">
                              <Tag className="w-3 h-3 mr-1" />
                              {task.category}
                            </span>
                          )}
                          {task.estimated_effort_value && (
                            <span className="flex items-center">
                              <Clock className="w-3 h-3 mr-1" />
                              {formatEstimatedEffort(task.estimated_effort_value, task.estimated_effort_unit)}
                            </span>
                          )}
                          {task.subtasks && task.subtasks.length > 0 && (
                            <span className="flex items-center">
                              <CheckSquare className="w-3 h-3 mr-1" />
                              {task.subtasks.length} subtask{task.subtasks.length !== 1 ? 's' : ''}
                            </span>
                          )}
                          {task.is_milestone && (
                            <span className="flex items-center text-purple-600">
                              <AlertCircle className="w-3 h-3 mr-1" />
                              Milestone
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <p className="text-sm text-gray-600">
            {selectedIds.length > 0 && (
              <span className="font-medium text-purple-600">
                {selectedIds.length} task template{selectedIds.length !== 1 ? 's' : ''} will be linked
              </span>
            )}
          </p>
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-white"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              Save Selection
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskTemplateSelector;
